import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { PawPrint, HeartHandshake, MapPin } from "lucide-react";
import { MaskLine } from "@/components/Reveal";
import { scrollToId } from "@/lib/api";

function HeroFact({ big, label }) {
  return (
    <div data-testid={`hero-fact-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <p className="font-serif text-2xl sm:text-3xl text-gold-soft">{big}</p>
      <p className="mt-1 font-mono text-[10px] sm:text-xs uppercase tracking-[0.18em] text-sand/60">{label}</p>
    </div>
  );
}

export default function Hero({ onDonate }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen overflow-hidden bg-pine-deep grain">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src="/sections/hero_sunset.webp"
          alt="A dog sitting on a mountain overlook at sunset, gazing over the valley"
          className="h-[120%] w-full object-cover"
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 3, ease: [0.22, 1, 0.36, 1] }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal/50 via-charcoal/25 to-pine-deep/90" />
      <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/35 to-transparent" />

      <motion.div style={{ opacity: contentOpacity }} className="relative z-10 mx-auto flex min-h-screen max-w-7xl flex-col justify-center px-4 pt-24 pb-16 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="mb-8 flex items-center gap-4"
          >
            <img src="/logo.png" alt="Wag n Tails Mountain Retreat Rescue logo"
              data-testid="hero-logo"
              className="h-24 w-24 sm:h-28 sm:w-28 rounded-full object-cover ring-4 ring-gold/60 shadow-2xl shadow-gold/20 animate-float-slow" />
            <div>
              <p className="dark-eyebrow flex items-center gap-2" data-testid="hero-eyebrow">
                <MapPin className="h-3.5 w-3.5" /> 501(c)(3) · Maryland Mountains
              </p>
              <p className="mt-1 font-serif text-lg italic text-sand/80">Wag n Tails Mountain Retreat Rescue</p>
            </div>
          </motion.div>

          <h1 className="font-serif text-4xl leading-[1.08] text-sand sm:text-6xl lg:text-7xl tracking-tight text-balance">
            <MaskLine delay={0.3}>Every tail deserves</MaskLine>
            <MaskLine delay={0.45}>
              a <span className="italic text-gold-soft">mountain</span> to
            </MaskLine>
            <MaskLine delay={0.6}>run on.</MaskLine>
          </h1>

          <MaskLine delay={0.8} className="mt-6 max-w-xl">
            <span className="text-base sm:text-lg leading-relaxed text-sand/75">
              We rescue, rehabilitate and rehome dogs and cats
              in the mountains of Maryland — where second chances come with fresh snow, warm kennels and someone who stays up all night if they need to.
            </span>
          </MaskLine>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <button data-testid="hero-animals-cta" onClick={() => scrollToId("animals")}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-sand px-8 py-4 font-bold text-pine transition-all hover:bg-white hover:scale-[1.03] active:scale-95">
              <PawPrint className="h-5 w-5 transition-transform group-hover:rotate-12" />
              Meet Our Animals
            </button>
            <button data-testid="hero-donate-cta" onClick={onDonate}
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-bold text-charcoal transition-all hover:bg-gold-soft hover:scale-[1.03] active:scale-95">
              <HeartHandshake className="h-5 w-5 transition-transform group-hover:rotate-12" />
              Donate to Save Lives
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-16 grid grid-cols-2 gap-6 border-t border-sand/15 pt-8 sm:grid-cols-4"
        >
          <HeroFact big="100%" label="Of every gift to the animals" />
          <HeroFact big="501(c)(3)" label="Registered nonprofit" />
          <HeroFact big="Dogs & Cats" label="Rescue & rehoming" />
          <HeroFact big="Maryland" label="Mountain sanctuary" />
        </motion.div>
      </motion.div>
    </section>
  );
}
